import { ArrowUpRight, Scale, Gavel, ShieldCheck } from "lucide-react";

const PC_URL = "https://privatecourt.ai";
const PC_IMG = "https://images.unsplash.com/photo-1589829545856-d10d557cf95f?auto=format&fit=crop&w=1200&q=60";

/* ---------- Variant 1: Wide banner (used after stats / filters) ---------- */
export const PrivateCourtBanner = ({ testid = "ad-privatecourt-banner" }) => (
  <a
    href={PC_URL}
    target="_blank"
    rel="sponsored noreferrer"
    data-testid={testid}
    className="group relative block border border-[#0A0A0A] bg-[#0A0A0A] text-white overflow-hidden hover:border-[#FF3333] transition-colors"
  >
    <div className="absolute inset-0 opacity-30 group-hover:opacity-40 transition-opacity">
      <img
        src={PC_IMG}
        alt=""
        className="w-full h-full object-cover"
        loading="lazy"
        aria-hidden
      />
    </div>
    <div className="relative grid grid-cols-1 md:grid-cols-[auto_1fr_auto] items-center gap-6 p-6 md:p-8">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 bg-white text-[#0A0A0A] flex items-center justify-center flex-shrink-0">
          <Gavel className="w-6 h-6" strokeWidth={2.25} />
        </div>
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/60">
            Sponsored · Advertisement
          </div>
          <div className="font-display font-black text-xl tracking-tight">
            PRIVATECOURT<span className="text-[#FF3333]">.AI</span>
          </div>
        </div>
      </div>
      <div>
        <div className="font-display font-bold text-lg md:text-xl leading-tight">
          Stuck with a defaulting borrower? Resolve loan disputes in days, not years.
        </div>
        <div className="text-sm text-white/70 mt-1.5 font-mono">
          AI-assisted online dispute resolution · Built for recovery & legal teams
        </div>
      </div>
      <div className="inline-flex items-center gap-2 bg-white text-[#0A0A0A] px-5 py-3 font-medium group-hover:bg-[#FF3333] group-hover:text-white transition-colors whitespace-nowrap">
        Open PrivateCourt <ArrowUpRight className="w-4 h-4" />
      </div>
    </div>
  </a>
);

/* ---------- Variant 2: Compact inline row (between table rows) ---------- */
export const PrivateCourtInline = ({ testid = "ad-privatecourt-inline" }) => (
  <a
    href={PC_URL}
    target="_blank"
    rel="sponsored noreferrer"
    data-testid={testid}
    className="group flex items-center justify-between gap-4 border-y-2 border-[#0A0A0A] bg-[#FFF8E7] hover:bg-[#FFE9A7] transition-colors px-4 py-3"
  >
    <div className="flex items-center gap-3 min-w-0">
      <div className="w-9 h-9 bg-[#0A0A0A] text-white flex items-center justify-center flex-shrink-0">
        <Scale className="w-4 h-4" strokeWidth={2.25} />
      </div>
      <div className="min-w-0">
        <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#4B5563]">
          Sponsored · PRIVATECOURT.AI
        </div>
        <div className="font-display font-bold text-sm leading-tight truncate">
          Skip the NCLT queue — settle recovery disputes online with AI arbitration.
        </div>
      </div>
    </div>
    <span className="inline-flex items-center gap-1 font-mono text-[11px] uppercase tracking-[0.15em] text-[#0A0A0A] group-hover:text-[#002FA7] whitespace-nowrap">
      Visit site <ArrowUpRight className="w-3.5 h-3.5" />
    </span>
  </a>
);

/* ---------- Variant 3: Sidebar/Drawer card ---------- */
export const PrivateCourtSidebar = ({ testid = "ad-privatecourt-sidebar" }) => (
  <a
    href={PC_URL}
    target="_blank"
    rel="sponsored noreferrer"
    data-testid={testid}
    className="group block border-2 border-[#0A0A0A] bg-white hover:bg-[#0A0A0A] hover:text-white transition-colors"
  >
    <div className="relative aspect-[16/7] overflow-hidden border-b-2 border-[#0A0A0A] group-hover:border-white">
      <img
        src={PC_IMG}
        alt=""
        className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-300"
        loading="lazy"
        aria-hidden
      />
      <div className="absolute top-2 left-2 bg-white text-[#0A0A0A] px-2 py-1 font-mono text-[9px] uppercase tracking-[0.2em] font-bold">
        Sponsored
      </div>
    </div>
    <div className="p-5">
      <div className="flex items-center gap-2 mb-2">
        <ShieldCheck className="w-4 h-4 text-[#FF3333]" />
        <span className="font-display font-black text-sm tracking-tight">
          PRIVATECOURT<span className="text-[#FF3333]">.AI</span>
        </span>
      </div>
      <div className="font-display font-bold text-base leading-tight mb-2">
        Can't reach the Recovery Head? Initiate arbitration online instead.
      </div>
      <div className="font-mono text-[11px] text-[#4B5563] group-hover:text-white/70">
        AI dispute resolution · SARFAESI support · 72-hour turnaround for EMI defaults.
      </div>
      <div className="mt-4 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.15em] underline underline-offset-4">
        Open PrivateCourt <ArrowUpRight className="w-3.5 h-3.5" />
      </div>
    </div>
  </a>
);

/* ---------- Variant 4: Footer strip ---------- */
export const PrivateCourtFooterStrip = ({ testid = "ad-privatecourt-footer" }) => (
  <a
    href={PC_URL}
    target="_blank"
    rel="sponsored noreferrer"
    data-testid={testid}
    className="group block border-t border-b border-[#FF3333] bg-[#FF3333] text-white hover:bg-[#CC0000] transition-colors"
  >
    <div className="max-w-[1440px] mx-auto px-6 md:px-8 lg:px-12 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
      <div className="flex items-center gap-3">
        <span className="font-mono text-[10px] uppercase tracking-[0.25em] bg-white text-[#FF3333] px-2 py-1 font-bold">
          Ad
        </span>
        <span className="font-display font-bold text-sm md:text-base">
          PRIVATECOURT.AI — India's fastest online dispute resolution for bank & NBFC recovery matters.
        </span>
      </div>
      <span className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.15em] whitespace-nowrap">
        Start a case <ArrowUpRight className="w-4 h-4" />
      </span>
    </div>
  </a>
);
